import React, { useState } from 'react';
import { Typography, Tooltip } from 'antd';
import { StepType, PROTOCOL_NODE_CONFIG } from '../../../../../types/lab/ProtocolTypes';
import { PROTOCOL_STEP_ICONS } from './protocolIcons';

const { Text } = Typography;

const FLOW_CONTROL_TYPES: StepType[] = ['start', 'conditional', 'end'];
const LAB_STEP_TYPES: StepType[] = ['manual', 'automated', 'qc', 'results', 'aliquot'];

interface ProtocolNodePaletteProps {
  onDragStart: (event: React.DragEvent, nodeType: StepType) => void;
}

const PaletteItem: React.FC<{
  stepType: StepType;
  onDragStart: (event: React.DragEvent, nodeType: StepType) => void;
}> = ({ stepType, onDragStart }) => {
  const cfg = PROTOCOL_NODE_CONFIG[stepType];
  const [hovered, setHovered] = useState(false);

  return (
    <Tooltip title={cfg.description} placement="right">
      <div
        draggable
        onDragStart={(e) => onDragStart(e, stepType)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 10px',
          borderRadius: 8,
          border: `1.5px solid ${cfg.color}${hovered ? '44' : '22'}`,
          background: hovered ? `${cfg.color}22` : `${cfg.color}10`,
          cursor: 'grab',
          marginBottom: 6,
          transition: 'background 0.15s, border-color 0.15s',
          userSelect: 'none',
        }}
      >
        <span style={{ fontSize: 15, color: cfg.color, flexShrink: 0 }}>
          {PROTOCOL_STEP_ICONS[stepType]}
        </span>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--mg-text)' }}>
          {cfg.label}
        </span>
      </div>
    </Tooltip>
  );
};

const SectionHeading: React.FC<{ label: string }> = ({ label }) => (
  <div style={{ padding: '10px 10px 4px' }}>
    <Text
      style={{
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: 0.8,
        textTransform: 'uppercase',
        color: 'var(--mg-text-secondary)',
      }}
    >
      {label}
    </Text>
  </div>
);

const ProtocolNodePalette: React.FC<ProtocolNodePaletteProps> = ({ onDragStart }) => {
  return (
    <div style={{ padding: '8px 10px' }}>
      <SectionHeading label="Flow Control" />
      {FLOW_CONTROL_TYPES.map((t) => (
        <PaletteItem key={t} stepType={t} onDragStart={onDragStart} />
      ))}

      <SectionHeading label="Lab Steps" />
      {LAB_STEP_TYPES.map((t) => (
        <PaletteItem key={t} stepType={t} onDragStart={onDragStart} />
      ))}

      {/* Tips */}
      <div
        style={{
          marginTop: 16,
          padding: '8px 10px',
          borderRadius: 8,
          background: 'var(--mg-bg-subtle)',
          border: '1px solid var(--mg-border)',
          fontSize: 11,
          color: 'var(--mg-text-secondary)',
          lineHeight: 1.5,
        }}
      >
        <strong style={{ display: 'block', marginBottom: 4 }}>Tips</strong>
        Drag nodes onto the canvas to build your protocol workflow.
        <br />
        Click a node to configure it.
        <br />
        Connect nodes by dragging from a handle (◉) to another.
      </div>
    </div>
  );
};

export default ProtocolNodePalette;
